import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import movMonedaService from '../../services/movMonedaService';
import tipoMonedaService from '../../services/tipoMonedaService';
import { normalizarFechaCotizacion } from '../../utils/cotizacionDia';

export interface CotizacionDiaDialogProps {
  open: boolean;
  codigosMoneda: number[];
  fecha: string;
  onRegistradas: (cotizaciones: Record<number, number>) => void;
  onCancel: () => void;
}

const formatFechaDisplay = (fecha: string): string => {
  const d = new Date(`${fecha}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('es-AR') : fecha;
};

const CotizacionDiaDialog: React.FC<CotizacionDiaDialogProps> = ({
  open,
  codigosMoneda,
  fecha,
  onRegistradas,
  onCancel,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fechaNorm = normalizarFechaCotizacion(fecha);
  const [labels, setLabels] = useState<Record<number, string>>({});
  const [valores, setValores] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial: Record<number, string> = {};
    codigosMoneda.forEach((c) => {
      initial[c] = '';
    });
    setValores(initial);

    let cancelled = false;
    (async () => {
      setLoadingLabels(true);
      const next: Record<number, string> = {};
      await Promise.all(
        codigosMoneda.map(async (codigo) => {
          try {
            const moneda = await tipoMonedaService.getTipoMoneda(codigo);
            next[codigo] = `${moneda.codigoMoneda} - ${moneda.descripcion}`;
          } catch {
            next[codigo] = `Moneda ${codigo}`;
          }
        }),
      );
      if (!cancelled) {
        setLabels(next);
        setLoadingLabels(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, codigosMoneda]);

  const handleChange = (codigo: number, value: string) => {
    setValores((prev) => ({ ...prev, [codigo]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const registradas: Record<number, number> = {};

    for (const codigo of codigosMoneda) {
      const n = Number(String(valores[codigo] ?? '').replace(',', '.'));
      if (!Number.isFinite(n) || n < 0.01) {
        enqueueSnackbar(`Ingresá una cotización válida para ${labels[codigo] || `moneda ${codigo}`}`, {
          variant: 'warning',
        });
        return;
      }
      registradas[codigo] = n;
    }

    try {
      setSaving(true);
      for (const codigo of codigosMoneda) {
        const result = await movMonedaService.registrarCotizacionDia({
          codigoMoneda: codigo,
          cotizacion: registradas[codigo],
          fechaCotizacion: fechaNorm,
        });
        if (result.cotizacion > 0) {
          registradas[codigo] = result.cotizacion;
        }
      }
      enqueueSnackbar('Cotización del día registrada', { variant: 'success' });
      onRegistradas(registradas);
    } catch (error: unknown) {
      console.error('Error al registrar cotización del día:', error);
      const err = error as { response?: { data?: string | { message?: string } } };
      const msg =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Error al registrar la cotización del día';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth disableEscapeKeyDown={saving}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 600, color: '#1976d2' }}>
          Cotización del día requerida
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              No hay cotización cargada para el {formatFechaDisplay(fechaNorm)}. Registrala para continuar con la
              operación. Quedará vigente para el resto del día.
            </Alert>
            {loadingLabels ? (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              codigosMoneda.map((codigo) => (
                <Stack key={codigo} spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {labels[codigo] || `Moneda ${codigo}`}
                  </Typography>
                  <TextField
                    label="Cotización"
                    type="number"
                    value={valores[codigo] ?? ''}
                    onChange={(e) => handleChange(codigo, e.target.value)}
                    fullWidth
                    required
                    inputProps={{ step: '0.0001', min: '0.01' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Stack>
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={saving || loadingLabels} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={22} /> : 'Guardar cotización'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CotizacionDiaDialog;
